/**
 * Reading saved generated UIs (task 396).
 *
 * `GET /generated-endpoint-uis?endpointId=…` returns **parent** UIs with their
 * versions nested. The parent knows which endpoint it belongs to; the version
 * holds the `source` and `prompt` everything on screen actually renders. These
 * helpers do that hop in one place so no caller reaches for `source` on a parent
 * again — which is what left the Connect tab's Interface card empty.
 */
import type { GeneratedEndpointUi, GeneratedEndpointUiVersion } from '@/types/uiComposer';

/**
 * The current version of a UI: the one `headVersionId` points at, falling back
 * to the highest `version` number when the pointer is null or dangling.
 * Returns undefined for a parent with no versions.
 */
export function resolveHeadVersion(
  ui: GeneratedEndpointUi
): GeneratedEndpointUiVersion | undefined {
  const versions = ui.versions ?? [];
  if (versions.length === 0) return undefined;

  const head = ui.headVersionId
    ? versions.find(version => version.id === ui.headVersionId)
    : undefined;
  if (head) return head;

  return versions.reduce((highest, version) =>
    version.version > highest.version ? version : highest
  );
}

/**
 * Every version across every parent UI, oldest first — so v1 is the first entry
 * and the last entry is the newest save. Ordering is by the `version` column,
 * not by array position; ties across different parents fall back to `createdAt`
 * so a service with more than one saved UI still lists in a stable order.
 */
export function flattenVersions(
  uis: GeneratedEndpointUi[]
): GeneratedEndpointUiVersion[] {
  return uis
    .flatMap(ui => ui.versions ?? [])
    .sort(
      (a, b) =>
        a.version - b.version ||
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
}

/** The parent UI a version belongs to, for label fallbacks and delete calls. */
export function findParentUi(
  uis: GeneratedEndpointUi[],
  version: GeneratedEndpointUiVersion
): GeneratedEndpointUi | undefined {
  return uis.find(ui => ui.id === version.generatedUiId);
}

/**
 * What to show as a version's name: its design prompt, else the parent UI's
 * label, else the caller's placeholder.
 */
export function versionLabel(
  version: GeneratedEndpointUiVersion,
  parent: GeneratedEndpointUi | undefined,
  fallback: string
): string {
  return version.prompt?.trim() || parent?.label?.trim() || fallback;
}
